/**
 * ContextItemTypeIcon component
 * Displays an icon based on context item type
 */
import {
  Bug,
  GitBranch,
  GitPullRequest,
  FolderGit2,
  FileText,
  Link,
  StickyNote,
  BookOpen,
  HelpCircle,
  FileCheck,
  Network,
  BookMarked,
  Terminal,
  Target,
  Puzzle,
  ExternalLink,
  FileJson,
  Palette,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { CONTEXT_ITEM_TYPES, PROJECT_CONTEXT_ITEM_TYPES } from '@/types/chatbot'

export interface ContextItemTypeIconProps {
  /** Context item type string (e.g., "ado:workitem", "wiki:page") */
  type: string
  /** Additional CSS classes */
  className?: string
  /** Icon size - defaults to 'md' */
  size?: 'sm' | 'md' | 'lg'
}

interface TypeConfig {
  icon: LucideIcon
  color: string
  label: string
}

const typeConfigs: Record<string, TypeConfig> = {
  // Session context item types
  [CONTEXT_ITEM_TYPES.ADO_WORKITEM]: {
    icon: Bug,
    color: 'text-blue-500',
    label: 'Work Item',
  },
  [CONTEXT_ITEM_TYPES.ADO_PR]: {
    icon: GitPullRequest,
    color: 'text-purple-500',
    label: 'Pull Request',
  },
  [CONTEXT_ITEM_TYPES.ADO_REPO]: {
    icon: FolderGit2,
    color: 'text-orange-500',
    label: 'Repository',
  },
  [CONTEXT_ITEM_TYPES.ADO_BRANCH]: {
    icon: GitBranch,
    color: 'text-green-500',
    label: 'Branch',
  },
  [CONTEXT_ITEM_TYPES.WIKI_PAGE]: {
    icon: BookOpen,
    color: 'text-teal-500',
    label: 'Wiki Page',
  },
  [CONTEXT_ITEM_TYPES.FILE]: {
    icon: FileText,
    color: 'text-amber-500',
    label: 'File',
  },
  [CONTEXT_ITEM_TYPES.URL]: {
    icon: Link,
    color: 'text-cyan-500',
    label: 'URL',
  },
  [CONTEXT_ITEM_TYPES.NOTE]: {
    icon: StickyNote,
    color: 'text-yellow-500',
    label: 'Note',
  },
  
  // Project-specific context item types (knowledge base)
  [PROJECT_CONTEXT_ITEM_TYPES.WIKI_ADR]: {
    icon: FileCheck,
    color: 'text-emerald-500',
    label: 'ADR',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.WIKI_ARCH]: {
    icon: Network,
    color: 'text-indigo-500',
    label: 'Architecture',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.WIKI_GUIDE]: {
    icon: BookMarked,
    color: 'text-sky-500',
    label: 'Guide',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.WIKI_RUNBOOK]: {
    icon: Terminal,
    color: 'text-rose-500',
    label: 'Runbook',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.EPIC]: {
    icon: Target,
    color: 'text-violet-500',
    label: 'Epic',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.FEATURE]: {
    icon: Puzzle,
    color: 'text-fuchsia-500',
    label: 'Feature',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.DOC_EXTERNAL]: {
    icon: ExternalLink,
    color: 'text-slate-500',
    label: 'External Doc',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.DOC_API]: {
    icon: FileJson,
    color: 'text-lime-500',
    label: 'API Doc',
  },
  [PROJECT_CONTEXT_ITEM_TYPES.DOC_DESIGN]: {
    icon: Palette,
    color: 'text-pink-500',
    label: 'Design Doc',
  },
}

const defaultConfig: TypeConfig = {
  icon: HelpCircle,
  color: 'text-muted-foreground',
  label: 'Unknown',
}

const sizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

/**
 * Get the type configuration for a context item type.
 * Exported for use by other components.
 */
export function getTypeConfig(type: string): TypeConfig {
  return typeConfigs[type] || defaultConfig
}

/**
 * Get the display label for a context item type.
 */
export function getTypeLabel(type: string): string {
  return getTypeConfig(type).label
}

/**
 * Icon component that renders the appropriate icon for a context item type.
 */
export function ContextItemTypeIcon({
  type,
  className,
  size = 'md',
}: ContextItemTypeIconProps) {
  const config = getTypeConfig(type)
  const Icon = config.icon

  return (
    <Icon
      className={cn(sizeClasses[size], config.color, className)}
      aria-label={config.label}
    />
  )
}
